import React, { useState, useEffect } from 'react'
import FormInput from './FormInput'
import { setAppStatusBarDark } from '../utils/flutterStatusBar'
import SubmitButton from './SubmitButton'
import { toastUtil } from '../utils/toast'
import { mapApi } from '../api/map'
import { useSheetDragDismiss } from '../hooks/useSheetDragDismiss'
import SheetHeader from './SheetHeader'

interface PlaceSuggestBottomSheetProps {
  isOpen: boolean
  onClose: () => void
  /**
   * 시트를 열 때 상호명에 미리 채워 둘 값 — 보통 방금 검색한 말이다.
   *
   * 이 시트는 "검색했는데 그 가게가 없다"는 흐름에서만 열린다. 찾던 이름을
   * 방금 쳤는데 빈 칸에 다시 치게 하는 건 같은 걸 두 번 시키는 일이다.
   */
  initialStoreName?: string
}

const PlaceSuggestBottomSheet: React.FC<PlaceSuggestBottomSheetProps> = ({
  isOpen,
  onClose,
  initialStoreName = '',
}) => {
  const [storeName, setStoreName] = useState('')
  const [region, setRegion] = useState('')

  const handleStoreNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStoreName(e.target.value)
  }

  const handleRegionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRegion(e.target.value)
  }

  // 폼 검증: 상호명·지역 필수
  const isFormValid = () => {
    return storeName.trim().length > 0 && region.trim().length > 0
  }

  const handleClose = () => {
    // 폼 상태 초기화
    setStoreName('')
    setRegion('')
    onClose()
  }

  const handleComplete = async () => {
    // 검증 실패 시 첫 번째 에러 메시지 표시
    if (!storeName.trim()) {
      toastUtil.error('상호명을 입력해주세요')
      return
    }
    if (!region.trim()) {
      toastUtil.error('지역을 입력해주세요')
      return
    }

    // 검증 통과 시 장소 제안 API 호출
    try {
      await mapApi.suggestPlace({
        storeName: storeName.trim(),
        region: region.trim(),
      })
      toastUtil.success('제안이 완료되었습니다')
      handleClose()
    } catch (error: any) {
      // 예전엔 여기서 조용히 넘어갔다(인터셉터가 처리한다고 가정). 그런데
      // 인터셉터가 안 잡는 오류면 사용자는 아무 반응도 못 본다. 반드시 알린다.
      toastUtil.error(
        error?.response?.data?.message || '제안을 보내지 못했습니다'
      )
    }
  }

  useEffect(() => {
    setAppStatusBarDark(isOpen)
    return () => setAppStatusBarDark(false)
  }, [isOpen])

  // 열리는 순간에만 검색어를 채운다.
  // isOpen이 false여도 이 컴포넌트는 언마운트되지 않아(부모가 항상 렌더) state가
  // 그대로 남는다. isOpen을 의존성으로 두고 열릴 때만 넣어야, 사용자가 고쳐 쓴
  // 이름을 나중에 다시 덮어쓰지 않는다.
  useEffect(() => {
    if (!isOpen) return
    setStoreName(initialStoreName)
  }, [isOpen, initialStoreName])

  const { handleProps, sheetStyle } = useSheetDragDismiss({
    onClose: handleClose,
    enabled: isOpen,
  })

  if (!isOpen) return null

  return (
    <>
      {/* 오버레이 */}
      <div
        className="fixed inset-0 bg-black bg-opacity-40 z-[1040]"
        onClick={handleClose}
      />

      {/* 바텀 시트 컨테이너 */}
      <div className="fixed bottom-0 left-0 right-0 z-[1050] flex justify-center">
        <div className="app-container flex flex-col">
          {/* 바텀 시트 */}
          <div
            className={`
              bg-ivoryKey
              rounded-t-30px
              shadow-lg
              overflow-hidden
              flex
              flex-col
              w-full
            `}
            style={sheetStyle}
          >
            {/* 드래그 핸들 (아래로 끌면 닫힘) */}
            <div className="flex flex-col w-full" {...handleProps}>
              <div className="w-40px h-5px bg-d9d9d9 rounded-20px mx-auto mt-16px mb-16px"></div>
            </div>

            {/* 헤더 (공용) */}
            <SheetHeader title="장소 제안" />

            {/* 입력 필드 영역 */}
            <div className="px-20px pt-16px pb-32px flex flex-col">
              {/* 상호명 입력 */}
              <FormInput
                label="상호명"
                name="storeName"
                value={storeName}
                onChange={handleStoreNameChange}
                placeholder="상호명을 입력해주세요"
                marginTop={0}
                marginBottom={0}
              />

              {/* 지역 입력 */}
              <FormInput
                label="지역"
                name="region"
                value={region}
                onChange={handleRegionChange}
                placeholder="어느 지역에 있는지 입력해주세요"
                marginTop={20}
                marginBottom={0}
              />

              {/* 완료 버튼 */}
              <div className="mt-24px">
                <SubmitButton
                  text="완료"
                  disabled={!isFormValid()}
                  onSubmit={handleComplete}
                  onDisabled={handleComplete}
                  addClassName="rounded-20px"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default PlaceSuggestBottomSheet
